import { parseChecklist } from "./lmsLeadForm.js";

/** Pull a display label from a recorder action-item blob. */
export function actionItemText(item) {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return "Action item";
  return item.task || item.text || item.title || "Action item";
}

/** Best-effort summary string from the recorder's summary JSON/string. */
export function recordingSummary(recording) {
  const summary = recording?.summary;
  if (!summary) return "";
  if (typeof summary === "string") return summary.trim();
  if (Array.isArray(summary)) {
    return summary.map((item) => (typeof item === "string" ? item : item?.text || item?.summary || "")).filter(Boolean).join("\n");
  }
  if (typeof summary !== "object") return "";
  const candidates = [
    summary.discussion_summary,
    summary.short_summary,
    summary.summary,
    summary.overview,
    summary.quick_take,
    summary.executive_summary,
    summary.meeting_summary,
    summary.description,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim() && !/^no transcript content available/i.test(value.trim())) {
      return value.trim();
    }
  }
  for (const value of Object.values(summary)) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (Array.isArray(value)) {
      const text = value.filter((item) => typeof item === "string" && item.trim()).join("\n");
      if (text) return text;
    }
  }
  return "";
}

export function recordingDate(recording) {
  return recording?.recorded_at || recording?.created_at;
}

function clip(text, max = 160) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function participantNames(recording) {
  const raw = recording?.participants;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === "string") return entry.trim();
        if (!entry || typeof entry !== "object") return "";
        return String(entry.name || entry.display_name || entry.email || "").trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === "object") {
    const names = [];
    for (const value of Object.values(raw)) {
      if (typeof value === "string" && value.trim()) names.push(value.trim());
      else if (Array.isArray(value)) {
        for (const entry of value) {
          if (typeof entry === "string" && entry.trim()) names.push(entry.trim());
          else if (entry && typeof entry === "object") {
            const label = String(entry.name || entry.display_name || entry.email || "").trim();
            if (label) names.push(label);
          }
        }
      }
    }
    return names;
  }
  return [];
}

function noteSnippet(note) {
  const meta = note?.metadata || {};
  if (meta.rep_note) return clip(meta.rep_note, 180);
  const title = String(note?.title || "").trim();
  const body = String(note?.body || "").trim().replace(/\s+/g, " ");
  if (/^general update$/i.test(title) || /^legacy(?: test)? note/i.test(title)) {
    return clip(body, 180);
  }
  if (title && body) return clip(`${title} — ${body}`, 180);
  return clip(title || body, 180);
}

function noteTime(note) {
  return note?.created_at || note?.updated_at || null;
}

/** MEDDPICC is the reference. The playbook stage is where the rep goes to read the move. */
export const PLAYBOOK = {
  discovery: { slug: "discovery", label: "Discovery", meddpicc: "Identify Pain and Metrics" },
  validation: { slug: "validation", label: "Validation", meddpicc: "Decision Criteria" },
  stakeholder_expansion: { slug: "stakeholder_expansion", label: "Stakeholder Expansion", meddpicc: "Champion and Economic Buyer" },
  procurement: { slug: "procurement", label: "Procurement", meddpicc: "Paper Process" },
};

export function playbookHref(slug) {
  return `/playbook/?stage=${encodeURIComponent(slug)}`;
}

const UNIDENTIFIED = /^(unknown[_\s-]*participant|speaker)([_\s-]|$|\d)/i;

function roster(recording) {
  const raw = recording?.participants;
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    if (!entry || typeof entry !== "object") return null;
    const name = String(entry.name || "").trim();
    if (!name) return null;
    return {
      name,
      talk: Number(entry.talk_time_seconds) || 0,
      role: String(entry.role || "").trim(),
      unidentified: UNIDENTIFIED.test(name),
    };
  }).filter(Boolean);
}

function ourSideNames(recording, summary) {
  const names = new Set();
  const title = String(recording?.title || "");
  const logged = title.match(/logged by\s+([A-Za-z][A-Za-z.'-]+(?:\s+[A-Za-z][A-Za-z.'-]+)?)/i);
  if (logged) names.add(logged[1].toLowerCase());
  const text = `${summary || ""} ${title}`;
  for (const match of text.matchAll(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\s+from\s+InterviewGod/g)) {
    names.add(match[1].toLowerCase());
  }
  return names;
}

/**
 * A read for someone who was not in the room.
 * Access = a new named person on their side.
 * Engagement = talk time, or their name in the summary. Zero talk and no mention stays unclaimed.
 * Power = a role stored on the person. Empty role is not a guessed Champion or Economic Buyer.
 * Motion = an action item. A deadline makes it dated.
 */
export function buildRoomReads(recordings = []) {
  const sorted = [...recordings].sort((left, right) => {
    const a = new Date(recordingDate(left) || 0).getTime();
    const b = new Date(recordingDate(right) || 0).getTime();
    return a - b;
  });
  const seen = new Set();
  const reads = [];
  let anyExternal = false;
  let anyRole = false;

  for (const recording of sorted) {
    const summary = recordingSummary(recording);
    const ours = ourSideNames(recording, summary);
    const people = roster(recording);
    const external = [];
    let unidentified = 0;
    for (const person of people) {
      if (person.unidentified) {
        unidentified += 1;
        continue;
      }
      if (ours.has(person.name.toLowerCase())) continue;
      external.push(person);
    }
    const newcomers = external.filter((person) => {
      const key = person.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (newcomers.length) anyExternal = true;
    const engaged = external.filter((person) => person.talk > 0 || (summary && summary.toLowerCase().includes(person.name.toLowerCase())));
    const powered = external.filter((person) => person.role);
    if (powered.length) anyRole = true;
    const actions = (recording.action_items || []).filter((item) => item && (typeof item === "string" || typeof item === "object"));
    const dated = actions.filter((item) => item && typeof item === "object" && item.deadline);

    const bits = [];
    let playbook = PLAYBOOK.discovery;
    if (newcomers.length) {
      bits.push(`Access: ${newcomers.map((person) => person.name).join(", ")} named on their side for the first time.`);
      playbook = PLAYBOOK.stakeholder_expansion;
    }
    if (engaged.length) {
      const how = engaged.some((person) => person.talk > 0) ? "has talk time" : "is named in the summary";
      bits.push(`Engagement: ${engaged.map((person) => person.name).join(", ")} ${how}.`);
    } else if (external.length) {
      bits.push("Engagement is not shown. Talk time is 0 and the summary does not name them speaking.");
    }
    if (powered.length) {
      bits.push(`Power: ${powered.map((person) => `${person.name} (${person.role})`).join(", ")}.`);
    }
    if (dated.length) {
      bits.push(`Motion, dated: ${actionItemText(dated[0])}.`);
    } else if (actions.length) {
      bits.push(`Motion, no date: ${actionItemText(actions[0])}.`);
    }
    if (unidentified && !external.length) {
      bits.push(`${unidentified} speaker ${unidentified === 1 ? "label is" : "labels are"} still unidentified.`);
    }
    if (!bits.length && summary) bits.push(clip(summary, 180));
    if (!bits.length) continue;
    if (/demo/i.test(recording.title || "") && playbook.slug === "discovery") playbook = PLAYBOOK.validation;
    reads.push({
      id: recording.id,
      at: recordingDate(recording),
      text: bits.join(" "),
      playbook,
    });
  }

  const gaps = [];
  if (anyExternal && !anyRole) {
    gaps.push({
      text: "No role is stored for anyone on their side. MEDDPICC still needs a Champion and an Economic Buyer. The recording does not say which person is which.",
      playbook: PLAYBOOK.stakeholder_expansion,
    });
  }
  return { reads, gaps };
}

/**
 * Frontend-only progression from data the lead page already has.
 * No Radar / scoring — recordings + human notes + checklist only.
 */
export function buildCallContext({ recordings = [], notes = [], checklistValue = "" }) {
  const checklist = parseChecklist(checklistValue);
  const checklistByLabel = new Map(
    checklist.map((item) => [item.label.trim().toLowerCase(), item]),
  );

  const sortedRecordings = [...recordings].sort((left, right) => {
    const a = new Date(recordingDate(left) || 0).getTime();
    const b = new Date(recordingDate(right) || 0).getTime();
    return a - b;
  });

  // Recorder-imported notes already mirror a recording card — skip them so the
  // strip is not the same call twice. Human notes stay.
  const humanNotes = (notes || []).filter((note) => (note?.metadata || {}).source !== "meet_recorder");

  const timeline = [];
  const seenPeople = new Set();
  const whoAppeared = [];

  for (const recording of sortedRecordings) {
    const summaryForSide = recordingSummary(recording);
    const ours = ourSideNames(recording, summaryForSide);
    const people = participantNames(recording);
    const theirSide = people.filter((name) => !UNIDENTIFIED.test(name) && !ours.has(name.toLowerCase()));
    const newcomers = theirSide.filter((name) => {
      const key = name.toLowerCase();
      if (seenPeople.has(key)) return false;
      seenPeople.add(key);
      whoAppeared.push({ name, firstSeenAt: recordingDate(recording), recordingId: recording.id });
      return true;
    });
    const summary = recordingSummary(recording);
    const actions = (recording.action_items || []).map(actionItemText).filter(Boolean);
    timeline.push({
      kind: "recording",
      id: `recording-${recording.id}`,
      at: recordingDate(recording),
      recording,
      summary,
      people,
      newcomers,
      actions,
      whatMoved: newcomers.length
        ? `Access — new on their side: ${newcomers.join(", ")}`
        : summary
          ? clip(summary, 140)
          : "",
    });
  }

  for (const note of humanNotes) {
    timeline.push({
      kind: "note",
      id: `note-${note.id}`,
      at: noteTime(note),
      note,
      snippet: noteSnippet(note),
      whatMoved: noteSnippet(note),
    });
  }

  timeline.sort((left, right) => {
    const a = new Date(left.at || 0).getTime();
    const b = new Date(right.at || 0).getTime();
    return a - b;
  });

  const openFromChecklist = checklist
    .filter((item) => !item.done && item.label)
    .map((item) => ({ label: item.label, source: "checklist" }));

  const openFromRecordings = [];
  const seenOpen = new Set(openFromChecklist.map((item) => item.label.toLowerCase()));
  for (const recording of [...sortedRecordings].reverse()) {
    for (const raw of recording.action_items || []) {
      const label = actionItemText(raw).trim();
      if (!label) continue;
      const key = label.toLowerCase();
      const task = checklistByLabel.get(key);
      if (task?.done) continue;
      if (seenOpen.has(key)) continue;
      seenOpen.add(key);
      openFromRecordings.push({ label, source: "recording", recordingId: recording.id });
    }
  }

  const openCommitments = [...openFromChecklist, ...openFromRecordings];
  const nextSteps = openCommitments.slice(0, 3).map((item) => item.label);

  return {
    timeline,
    whoAppeared,
    openCommitments,
    nextSteps,
    checklist,
    room: buildRoomReads(sortedRecordings),
  };
}
